import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { COLORS } from '@traton/common-ui';
import { Announcement } from '@internal/backstage-plugin-announcement-common';
import { AnnouncementSeverityIcon } from './AnnouncementSeverityIcon';
import { AnnouncementContent } from './AnnouncementContent';

export const getCriticalityBg = (isDark: boolean): Record<string, string> => ({
  info: COLORS.light.TRATON_Blue_600,
  warning: isDark
    ? COLORS.dark.TRATON_Signal_Warning
    : COLORS.light.TRATON_Signal_Warning,
  critical: isDark
    ? COLORS.dark.TRATON_Signal_Red
    : COLORS.light.TRATON_Signal_Red,
});

interface AnnouncementBannerItemProps {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}

export const AnnouncementBannerItem = ({
  announcement,
  onDismiss,
}: AnnouncementBannerItemProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const bg = getCriticalityBg(isDark);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '13px 16px',
        gap: '24px',
        bgcolor: bg[announcement.criticality] ?? bg.info,
        borderBottom: `1px solid ${
          isDark
            ? COLORS.dark.TRATON_Warm_Grey_1000
            : COLORS.light.TRATON_Warm_Grey_50
        }`,
        width: '100%',
      }}
    >
      {/* Left: icon + text content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: '6px',
          flex: 1,
          minWidth: 0,
        }}
      >
        <AnnouncementSeverityIcon criticality={announcement.criticality} />
        <AnnouncementContent
          title={announcement.title}
          message={announcement.message}
          links={announcement.links}
        />
      </Box>

      {/* Close button — only shown when the announcement is dismissable */}
      {announcement.dismissable && (
        <IconButton
          size="small"
          onClick={() => onDismiss(announcement.id)}
          sx={{
            color: COLORS.light.TRATON_White,
            p: 0,
            flexShrink: 0,
          }}
          aria-label="Dismiss announcement"
        >
          <CloseIcon sx={{ fontSize: '24px' }} />
        </IconButton>
      )}
    </Box>
  );
};
